/**
 * InvestmentEditorPage Component
 *
 * Main page for editing investment details with three tabs:
 * 1. Context Tab - Basic information, objectives, milestones, sponsors
 * 2. Resources Tab - Resource allocation and planning
 * 3. Financials Tab - Costs and benefits analysis
 */

import {
  useInvestment,
  useUpdateInvestment,
  useChangeStatus,
  useRoadmapItems,
  useDeleteInvestment,
} from '@medsuite/api-client';
import { isRestrictedStatus } from '@medsuite/models';
import {
  StatusBadge,
  Button,
  Tabs,
  TabList,
  TabItem,
  TabPanels,
  TabPanel,
  Spinner,
  Alert,
  useToast,
  useDialog,
  ConfirmDeleteDialog,
} from '@medsuite/ui-components';
import React, { useState, useRef, useEffect, useLayoutEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';

import { usePermissions } from '../../hooks/usePermissions';

import InvestmentDecisionDialog from './components/InvestmentDecisionDialog';
import ScoringTab from './components/StrategySelector';
import { CommentsTab } from './tabs/CommentsTab';
import { ContextTab } from './tabs/ContextTab';
import { DecisionsTab } from './tabs/DecisionsTab';
import { FinancialsTab } from './tabs/FinancialsTab';
import { RelatedTab } from './tabs/RelatedTab';
import { ResourcesTab } from './tabs/ResourcesTab';
import { RisksTab } from './tabs/RisksTab';

import type { InvestmentStatus } from '@medsuite/models';

/**
 * InvestmentEditorPage Component
 */
export const InvestmentEditorPage: React.FC = () => {

  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const investmentId = parseInt(id || '0', 10);

  const [activeTab, setActiveTab] = useState(0);
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [showStatusMenu, setShowStatusMenu] = useState(false);
  const [pendingUpdates, setPendingUpdates] = useState<Record<string, unknown>>({});
  const [decisionDialogTarget, setDecisionDialogTarget] = useState<InvestmentStatus | null>(null);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);

  // Permissions
  const { canMakeStatusTransition, canManageStatus } = usePermissions();

  // Toast notifications
  const { addToast } = useToast();

  // Dialog hooks
  const { confirm } = useDialog();

  // Track if we just changed isEditing to restore scroll
  const isEditingChangedRef = useRef(false);

  // Store save functions from child components
  const saveFunctionsRef = useRef<{
    saveMilestones?: () => Promise<void>;
    saveSponsors?: () => Promise<void>;
    saveSuccessMeasures?: () => Promise<void>;
    saveResources?: () => Promise<void>;
  }>({});

  // Preserve scroll position across StrictMode unmount/remount cycles
  const scrollKey = `investmentEditor_${investmentId}_scroll`;

  useEffect(() => {
    // Restore scroll position from sessionStorage
    const savedScroll = sessionStorage.getItem(scrollKey);
    if (savedScroll) {
      const scrollPos = parseInt(savedScroll, 10);
      // Use requestAnimationFrame to ensure DOM is ready
      requestAnimationFrame(() => {
        window.scrollTo(0, scrollPos);
      });
    }

    // Save scroll position periodically
    const saveScroll = () => {
      sessionStorage.setItem(scrollKey, window.scrollY.toString());
    };

    window.addEventListener('scroll', saveScroll, { passive: true });

    return () => {
      // Save final scroll position before unmount
      sessionStorage.setItem(scrollKey, window.scrollY.toString());
      window.removeEventListener('scroll', saveScroll);
    };
  }, [scrollKey]);

  // Restore scroll after isEditing changes cause re-render
  useLayoutEffect(() => {
    if (isEditingChangedRef.current) {
      const savedScroll = sessionStorage.getItem(scrollKey);
      if (savedScroll) {
        const scrollPos = parseInt(savedScroll, 10);
        // Only restore if we actually scrolled (more than 50px from top)
        // This prevents unnecessarily calling window.scrollTo() which can blur inputs
        if (scrollPos > 50) {
          window.scrollTo(0, scrollPos);
        }
      }
      isEditingChangedRef.current = false;
    }
  }, [isEditing, scrollKey]); // Only run when isEditing actually changes

  // Fetch investment data
  const {
    data: investment,
    isLoading,
    isError,
    error,
  } = useInvestment(investmentId, {
    enabled: !isNaN(investmentId) && investmentId > 0,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
  });

  // Check if this investment is on a roadmap
  const { data: roadmapItems = [] } = useRoadmapItems(
    { investment_id: investmentId },
    { enabled: !isNaN(investmentId) && investmentId > 0 }
  );
  const roadmapItem = roadmapItems[0]; // Get the first roadmap item if exists

  // Update investment mutation
  const updateInvestment = useUpdateInvestment();

  // Change status mutation
  const changeStatus = useChangeStatus(investmentId);

  // Delete investment mutation
  const deleteInvestment = useDeleteInvestment();

  // Handle delete
  const handleDelete = async () => {
    try {
      await deleteInvestment.mutateAsync(investmentId);
      addToast({
        message: 'Investment deleted successfully',
        variant: 'success',
        duration: 3000,
      });
      navigate('/');
    } catch (error) {
      console.error('Delete error:', error);
      addToast({
        message: 'Failed to delete investment. Please try again.',
        variant: 'error',
        duration: 5000,
      });
    }
    setShowDeleteDialog(false);
  };

  // Handle save
  const handleSave = async () => {
    if (!investment) {
      return;
    }

    // Save scroll position before save operation
    const scrollPosition = window.scrollY;
    setIsSaving(true);

    try {
      // IMPORTANT: Save all child data FIRST
      // (before updating investment, which triggers a refetch that can remount the component)
      if (saveFunctionsRef.current.saveMilestones) {
        await saveFunctionsRef.current.saveMilestones();
      }

      if (saveFunctionsRef.current.saveSponsors) {
        await saveFunctionsRef.current.saveSponsors();
      }

      if (saveFunctionsRef.current.saveSuccessMeasures) {
        await saveFunctionsRef.current.saveSuccessMeasures();
      }

      if (saveFunctionsRef.current.saveResources) {
        await saveFunctionsRef.current.saveResources();
      }

      // Then save investment fields if there are pending updates
      if (Object.keys(pendingUpdates).length > 0) {
        await updateInvestment.mutateAsync({
          id: investmentId,
          data: pendingUpdates,
        });
        setPendingUpdates({});
      }

      setIsEditing(false);
      setIsSaving(false);

      // Show success toast
      addToast({
        message: 'Changes saved successfully',
        variant: 'success',
        duration: 3000,
      });

      // Restore scroll position after all operations complete
      // Use setTimeout to ensure it runs after any React Query refetches
      setTimeout(() => {
        window.scrollTo(0, scrollPosition);
      }, 100);
    } catch (error) {
      console.error('Save error:', error);
      setIsSaving(false);

      // Show error toast
      addToast({
        message: 'Failed to save changes. Please try again.',
        variant: 'error',
        duration: 5000,
      });

      // Restore scroll even on error
      setTimeout(() => {
        window.scrollTo(0, scrollPosition);
      }, 100);
    }
  };

  // Stable callback for setIsEditing
  const handleSetIsEditing = useCallback((editing: boolean) => {
    if (window.scrollY > 50) {
      isEditingChangedRef.current = true;
    }
    setIsEditing(editing);
  }, []);

  // Stable callback for onUpdate
  const handleUpdate = useCallback((updates: Record<string, unknown>) => {
    setPendingUpdates((prev) => ({ ...prev, ...updates }));
  }, []);

  // Stable callback for registering save functions
  const handleRegisterSaveFunctions = useCallback((funcs: Record<string, () => Promise<void>>) => {
    saveFunctionsRef.current = {
      ...saveFunctionsRef.current,
      ...funcs,
    };
  }, []);

  // Handle cancel
  const handleCancel = async () => {
    if (isEditing) {
      const confirmCancel = await confirm({
        title: 'Unsaved Changes',
        message: 'You have unsaved changes. Are you sure you want to cancel?',
        variant: 'warning',
        confirmText: 'Discard Changes',
      });
      if (!confirmCancel) {
        return;
      }
    }
    navigate('/');
  };

  // Handle status change
  const handleStatusChange = async (newStatus: InvestmentStatus) => {
    if (!investment) {
      return;
    }

    // Check if this is a restricted status that requires DecisionMaker or Administrator role
    if (isRestrictedStatus(newStatus)) {
      if (!canManageStatus) {
        // User doesn't have permission - this shouldn't happen due to UI disabling
        return;
      }
      // Open decision dialog for restricted statuses
      setDecisionDialogTarget(newStatus);
      setShowStatusMenu(false);
      return;
    }

    // For non-restricted statuses, change directly
    try {
      await changeStatus.mutateAsync({
        new_status: newStatus,
      });
      setShowStatusMenu(false);
    } catch (error) {
      console.error('Status change error:', error);
    }
  };

  // Handle decision dialog success
  const handleDecisionSuccess = () => {
    setDecisionDialogTarget(null);
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="flex h-full items-center justify-center bg-gray-50 dark:bg-gray-900">
        <div className="text-center">
          <Spinner size="lg" />
          <p className="mt-4 text-gray-600 dark:text-gray-400">Loading investment...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (isError || !investment) {
    return (
      <div className="container mx-auto px-4 py-8 bg-gray-50 dark:bg-gray-900 min-h-screen">
        <Alert variant="error">
          <p className="font-semibold">Failed to load investment</p>
          <p className="text-sm">
            {error?.message || 'Investment not found or could not be loaded.'}
          </p>
        </Alert>
        <div className="mt-4">
          <Button onClick={() => navigate('/')}>Back to Home</Button>
        </div>
      </div>
    );
  }

  const statusOptions: InvestmentStatus[] = [
    'new',
    'submitted',
    'approved',
    'deferred',
    'rejected',
    'planned',
    'in_progress',
    'complete',
    'cancelled',
  ];

  return (
    <div className="h-full flex flex-col bg-gray-50 dark:bg-gray-900">
      {/* Header */}
      <div className="border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 px-6 py-4">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                {investment.title || 'Untitled Investment'}
              </h1>
              <StatusBadge status={investment.status} />
            </div>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              Investment ID: {investment.investment_id}
            </p>
          </div>

          {/* Action buttons */}
          <div className="flex gap-2">
            {/* Change Status Button */}
            <div className="relative">
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowStatusMenu(!showStatusMenu)}
                disabled={changeStatus.isPending}
              >
                {changeStatus.isPending ? 'Changing...' : 'Change Status'}
              </Button>

              {/* Status dropdown */}
              {showStatusMenu && !changeStatus.isPending && (
                <div className="absolute right-0 z-10 mt-2 w-64 rounded-md bg-white dark:bg-gray-800 shadow-lg ring-1 ring-black dark:ring-gray-700 ring-opacity-5">
                  <div
                    className="py-1"
                    role="menu"
                    aria-orientation="vertical"
                    aria-labelledby="status-menu"
                  >
                    {statusOptions.map((status) => {
                      const isCurrentStatus = status === investment.status;
                      const canTransition = canMakeStatusTransition(investment.status, status);
                      const isDisabled = isCurrentStatus || !canTransition;
                      const needsPermission = !canTransition && !isCurrentStatus;

                      return (
                        <div key={status} className="relative group">
                          <button
                            type="button"
                            className={`block w-full px-4 py-2 text-left text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed ${isDisabled ? 'opacity-50' : ''}`}
                            role="menuitem"
                            onClick={() => handleStatusChange(status)}
                            disabled={isDisabled}
                          >
                            <div className="flex items-center justify-between">
                              <StatusBadge status={status} size="sm" />
                              {needsPermission && (
                                <svg
                                  className="h-4 w-4 text-gray-400"
                                  fill="none"
                                  viewBox="0 0 24 24"
                                  stroke="currentColor"
                                  aria-label="Requires DecisionMaker permission"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                                  />
                                </svg>
                              )}
                            </div>
                          </button>
                          {/* Tooltip for restricted transitions */}
                          {needsPermission && (
                            <div className="absolute left-full top-1/2 -translate-y-1/2 ml-2 px-2 py-1 bg-gray-900 text-white text-xs rounded whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-20">
                              Requires DecisionMaker permission
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>

            <Button type="button" variant="outline" onClick={handleCancel}>
              Back to List
            </Button>

            <Button
              type="button"
              variant="danger"
              onClick={() => setShowDeleteDialog(true)}
              disabled={deleteInvestment.isPending}
            >
              Delete
            </Button>

            {roadmapItem && (
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate(`/roadmap/${roadmapItem.roadmap_id}`)}
              >
                Show on Roadmap
              </Button>
            )}

            <Button type="button" onClick={handleSave} disabled={!isEditing || isSaving}>
              {isSaving ? (
                <span className="flex items-center gap-2">
                  <Spinner size="sm" />
                  Saving...
                </span>
              ) : (
                'Save'
              )}
            </Button>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex-1 overflow-hidden bg-gray-50 dark:bg-gray-900">
        <div className="container mx-auto h-full px-6 py-6">
          <Tabs selectedIndex={activeTab} onChange={setActiveTab}>
            <TabList>
              <TabItem>Context</TabItem>
              <TabItem>Resources</TabItem>
              <TabItem>Financials</TabItem>
              <TabItem>Scoring</TabItem>
              <TabItem>Risks</TabItem>
              <TabItem>Comments</TabItem>
              <TabItem>Decisions</TabItem>
              <TabItem>Related</TabItem>
            </TabList>

            <TabPanels className="h-[calc(100vh-250px)] overflow-y-auto">
              {/* Tab 1: Context */}
              <TabPanel>
                <ContextTab
                  key={`context-${investment.investment_id}`}
                  investment={investment}
                  onUpdate={handleUpdate}
                  isEditing={isEditing}
                  setIsEditing={handleSetIsEditing}
                  onRegisterSaveFunctions={handleRegisterSaveFunctions}
                />
              </TabPanel>

              {/* Tab 2: Resources */}
              <TabPanel>
                <ResourcesTab
                  investment={investment}
                  isEditing={isEditing}
                  setIsEditing={handleSetIsEditing}
                  onRegisterSaveFunctions={handleRegisterSaveFunctions}
                />
              </TabPanel>

              {/* Tab 3: Financials */}
              <TabPanel>
                <FinancialsTab
                  investment={investment}
                  isEditing={isEditing}
                  setIsEditing={handleSetIsEditing}
                />
              </TabPanel>

              {/* Tab 4: Scoring */}
              <TabPanel>
                <ScoringTab investment={investment} />
              </TabPanel>

              {/* Tab 5: Risks */}
              <TabPanel>
                <RisksTab
                  investment={investment}
                  isEditing={isEditing}
                  setIsEditing={handleSetIsEditing}
                />
              </TabPanel>

              {/* Tab 6: Comments */}
              <TabPanel>
                <CommentsTab investment={investment} />
              </TabPanel>

              {/* Tab 7: Decisions */}
              <TabPanel>
                <DecisionsTab investment={investment} />
              </TabPanel>

              {/* Tab 8: Related (Attachments + Links) */}
              <TabPanel>
                <RelatedTab investment={investment} />
              </TabPanel>
            </TabPanels>
          </Tabs>
        </div>
      </div>

      {/* Investment Decision Dialog */}
      {decisionDialogTarget && (
        <InvestmentDecisionDialog
          investmentId={investmentId}
          investmentTitle={investment.title || 'Untitled Investment'}
          currentStatus={investment.status}
          targetStatus={decisionDialogTarget}
          isOpen={!!decisionDialogTarget}
          onClose={() => setDecisionDialogTarget(null)}
          onSuccess={handleDecisionSuccess}
        />
      )}

      {/* Delete Investment Dialog */}
      <ConfirmDeleteDialog
        isOpen={showDeleteDialog}
        onClose={() => setShowDeleteDialog(false)}
        onConfirm={handleDelete}
        title="Delete Investment"
        itemName={investment.title || 'Untitled Investment'}
        message={
          <div>
            <p className="mb-2">
              This action <strong>cannot be undone</strong>. This will permanently delete the
              investment and all associated data including:
            </p>
            <ul className="list-disc list-inside text-sm space-y-1">
              <li>Milestones and success measures</li>
              <li>Sponsors and resources</li>
              <li>Costs and benefits</li>
              <li>Scores and comments</li>
              <li>Decisions and status history</li>
            </ul>
          </div>
        }
        confirmText="Delete Investment"
        isLoading={deleteInvestment.isPending}
      />
    </div>
  );
};

export default InvestmentEditorPage;
